/**
 * AdminPartners.tsx — Partners (allowlist) CRUD section.
 *
 * Rules followed (per security review):
 * - NO optimistic updates: every mutation is followed by a full list re-fetch.
 * - Expiry: "YYYY-MM-DD" → "YYYY-MM-DDT23:59:59.999Z" (end of day UTC).
 * - Status three-way: Expired (entry.expired) / Enabled / Disabled.
 * - 403 from any admin call → authoritative "session no longer authorized" state.
 * - All user-supplied values rendered as React text; no dangerouslySetInnerHTML.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminFetch, type AllowlistEntry } from '../lib/adminApi';

// ─── Types ──────────────────────────────────────────────────────────────────

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'forbidden' }
  | { phase: 'ready'; entries: AllowlistEntry[] };

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Convert YYYY-MM-DD date-input value to end-of-day UTC ISO string. */
function dateToExpiresAt(dateValue: string): string {
  return `${dateValue}T23:59:59.999Z`;
}

/** Format an ISO string as a readable date, handling null. */
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return iso;
  }
}

/** Relative time hint (e.g. "3 days ago", "in 2 months"). */
function relativeHint(iso: string | null): string | null {
  if (!iso) return null;
  try {
    const diff = new Date(iso).getTime() - Date.now();
    if (!Number.isFinite(diff)) return null;
    const abs = Math.abs(diff);
    const past = diff < 0;
    const days = Math.round(abs / 86_400_000);
    if (days === 0) return past ? 'today' : 'today';
    if (days < 30) return past ? `${days}d ago` : `in ${days}d`;
    const months = Math.round(days / 30);
    return past ? `${months}mo ago` : `in ${months}mo`;
  } catch {
    return null;
  }
}

// ─── AddForm ─────────────────────────────────────────────────────────────────

interface AddFormProps {
  onAdded: () => void;
  onForbidden: () => void;
  getIdToken: () => Promise<string | null>;
}

function AddForm({ onAdded, onForbidden, getIdToken }: AddFormProps) {
  const [type, setType] = useState<'domain' | 'email'>('domain');
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (submitting) return;
      setFormError(null);
      setSubmitting(true);

      const body: Record<string, unknown> = { type, value: value.trim() };
      if (label.trim()) body.label = label.trim();
      if (expiryDate) body.expiresAt = dateToExpiresAt(expiryDate);

      const result = await adminFetch(getIdToken, 'POST', '/api/admin/allowlist', body);
      setSubmitting(false);

      if (result.status === 403) {
        onForbidden();
        return;
      }
      if (result.status === 400) {
        setFormError(result.data?.detail ?? result.data?.error ?? 'Invalid request.');
        return;
      }
      if (result.status === 409) {
        setFormError(result.data?.detail ?? 'Duplicate entry already exists.');
        return;
      }
      if (!result.ok) {
        setFormError(`Unexpected error (HTTP ${result.status}).`);
        return;
      }

      // Success: reset form and re-fetch.
      setValue('');
      setLabel('');
      setExpiryDate('');
      setFormError(null);
      onAdded();
    },
    [submitting, type, value, label, expiryDate, getIdToken, onAdded, onForbidden],
  );

  return (
    <form className="admin__add-form" onSubmit={handleSubmit} aria-label="Add allowlist entry">
      <div className="admin__add-row">
        {/* Type toggle */}
        <fieldset className="admin__type-toggle" aria-label="Entry type">
          <legend className="admin__field-label">Type</legend>
          <div className="admin__toggle-btns">
            <button
              type="button"
              className={`admin__toggle-btn${type === 'domain' ? ' admin__toggle-btn--active' : ''}`}
              onClick={() => setType('domain')}
              aria-pressed={type === 'domain'}
            >
              Domain
            </button>
            <button
              type="button"
              className={`admin__toggle-btn${type === 'email' ? ' admin__toggle-btn--active' : ''}`}
              onClick={() => setType('email')}
              aria-pressed={type === 'email'}
            >
              Email
            </button>
          </div>
          {type === 'domain' && (
            <p className="admin__field-hint">
              Free providers (gmail, outlook, etc.) must be added as individual emails, not domains.
            </p>
          )}
        </fieldset>

        {/* Value */}
        <label className="admin__field">
          <span className="admin__field-label">
            {type === 'domain' ? 'Domain' : 'Email address'}
          </span>
          <input
            className="admin__input"
            type={type === 'email' ? 'email' : 'text'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={type === 'domain' ? 'acme.com' : 'alice@acme.com'}
            required
            autoComplete="off"
            spellCheck={false}
          />
        </label>

        {/* Label */}
        <label className="admin__field">
          <span className="admin__field-label">Label (optional)</span>
          <input
            className="admin__input"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Acme Corp trial"
            autoComplete="off"
          />
        </label>

        {/* Expiry */}
        <label className="admin__field">
          <span className="admin__field-label">Expires (end of day, UTC) — optional</span>
          <input
            className="admin__input"
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
          />
        </label>

        <div className="admin__add-actions">
          <button
            type="submit"
            className="admin__btn admin__btn--primary"
            disabled={submitting}
            aria-disabled={submitting}
          >
            {submitting ? 'Adding…' : 'Add entry'}
          </button>
        </div>
      </div>

      {formError && (
        <p className="admin__form-error" role="alert">
          {formError}
        </p>
      )}
    </form>
  );
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

function StatusBadge({ entry }: { entry: AllowlistEntry }) {
  if (entry.expired) {
    return <span className="admin__badge admin__badge--expired">Expired</span>;
  }
  if (entry.enabled) {
    return <span className="admin__badge admin__badge--enabled">Enabled</span>;
  }
  return <span className="admin__badge admin__badge--disabled">Disabled</span>;
}

// ─── EntryRow ────────────────────────────────────────────────────────────────

interface EntryRowProps {
  entry: AllowlistEntry;
  onMutated: () => void;
  onForbidden: () => void;
  getIdToken: () => Promise<string | null>;
}

function EntryRow({ entry, onMutated, onForbidden, getIdToken }: EntryRowProps) {
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  const handleToggle = useCallback(async () => {
    if (toggling || deleting) return;
    setRowError(null);
    setToggling(true);
    const result = await adminFetch(getIdToken, 'PATCH', `/api/admin/allowlist/${entry.id}`, {
      enabled: !entry.enabled,
    });
    setToggling(false);
    if (result.status === 403) { onForbidden(); return; }
    if (!result.ok) {
      setRowError(result.data?.detail ?? result.data?.error ?? `Error ${result.status}`);
      return;
    }
    onMutated();
  }, [toggling, deleting, entry.id, entry.enabled, getIdToken, onMutated, onForbidden]);

  const handleDelete = useCallback(async () => {
    if (toggling || deleting) return;
    const confirmed = window.confirm(
      `Delete allowlist entry for "${entry.value}"?`,
    );
    if (!confirmed) return;
    setRowError(null);
    setDeleting(true);
    const result = await adminFetch(getIdToken, 'DELETE', `/api/admin/allowlist/${entry.id}`);
    setDeleting(false);
    if (result.status === 403) { onForbidden(); return; }
    if (!result.ok) {
      setRowError(result.data?.detail ?? result.data?.error ?? `Error ${result.status}`);
      return;
    }
    onMutated();
  }, [toggling, deleting, entry.id, entry.value, getIdToken, onMutated, onForbidden]);

  const expiryHint = relativeHint(entry.expiresAt);

  return (
    <tr className="admin__tr">
      <td className="admin__td admin__td--type">
        <span className="admin__type-pill">{entry.type}</span>
      </td>
      <td className="admin__td admin__td--value">
        <span className="admin__mono">{entry.value}</span>
      </td>
      <td className="admin__td admin__td--label">
        {entry.label ?? <span className="admin__muted">—</span>}
      </td>
      <td className="admin__td admin__td--status">
        <StatusBadge entry={entry} />
      </td>
      <td className="admin__td admin__td--expiry">
        {entry.expiresAt ? (
          <span title={entry.expiresAt}>
            {fmtDate(entry.expiresAt)}
            {expiryHint && <span className="admin__muted"> ({expiryHint})</span>}
          </span>
        ) : (
          <span className="admin__muted">—</span>
        )}
      </td>
      <td className="admin__td admin__td--added-by">
        {entry.addedBy ? (
          <span className="admin__muted-sm" title={entry.addedAt ?? undefined}>
            {entry.addedBy}
          </span>
        ) : (
          <span className="admin__muted">—</span>
        )}
      </td>
      <td className="admin__td admin__td--actions">
        <div className="admin__row-actions">
          <button
            type="button"
            className={`admin__btn admin__btn--sm${entry.enabled ? ' admin__btn--muted' : ' admin__btn--secondary'}`}
            onClick={handleToggle}
            disabled={toggling || deleting}
            aria-label={entry.enabled ? `Disable ${entry.value}` : `Enable ${entry.value}`}
          >
            {toggling ? '…' : entry.enabled ? 'Disable' : 'Enable'}
          </button>
          <button
            type="button"
            className="admin__btn admin__btn--sm admin__btn--danger"
            onClick={handleDelete}
            disabled={toggling || deleting}
            aria-label={`Delete allowlist entry for ${entry.value}`}
          >
            {deleting ? '…' : 'Delete'}
          </button>
        </div>
        {rowError && <p className="admin__row-error">{rowError}</p>}
      </td>
    </tr>
  );
}

// ─── AdminPartners (main export) ─────────────────────────────────────────────

export default function AdminPartners() {
  const { getIdToken } = useAuth();
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [reloadTick, setReloadTick] = useState(0);
  // Once ANY admin call returns 403, the forbidden state is STICKY: a GET still
  // in flight or a later mutation success must not overwrite it (gpt55 POST-CODE).
  const forbiddenRef = useRef(false);

  const triggerReload = useCallback(() => {
    if (forbiddenRef.current) return;
    setReloadTick((t) => t + 1);
  }, []);

  const handleForbidden = useCallback(() => {
    forbiddenRef.current = true;
    setState({ phase: 'forbidden' });
  }, []);

  // Fetch the list on mount and after every mutation.
  useEffect(() => {
    if (forbiddenRef.current) return;
    let cancelled = false;
    setState({ phase: 'loading' });

    (async () => {
      const result = await adminFetch(getIdToken, 'GET', '/api/admin/allowlist');
      if (cancelled || forbiddenRef.current) return;

      if (result.status === 403) {
        forbiddenRef.current = true;
        setState({ phase: 'forbidden' });
        return;
      }
      if (!result.ok) {
        const msg =
          result.status === 0
            ? (result.data?.message ?? 'Network error — check your connection.')
            : `Failed to load (HTTP ${result.status}).`;
        setState({ phase: 'error', message: msg });
        return;
      }

      const entries: AllowlistEntry[] = Array.isArray(result.data?.entries)
        ? (result.data.entries as AllowlistEntry[])
        : [];
      setState({ phase: 'ready', entries });
    })();

    return () => { cancelled = true; };
  // reloadTick intentionally included so we re-fetch on every triggerReload() call.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getIdToken, reloadTick]);

  // ── Forbidden state ────────────────────────────────────────────────────────
  if (state.phase === 'forbidden') {
    return (
      <div className="admin__forbidden" role="alert">
        <p className="admin__forbidden-title">Session no longer authorized</p>
        <p className="admin__forbidden-body">
          Your session is no longer authorized (staff access may have changed). Sign in again.
        </p>
      </div>
    );
  }

  // ── Sorted entries (server sends newest-first; sort a copy, don't mutate) ──
  const sorted =
    state.phase === 'ready'
      ? [...state.entries].sort((a, b) => {
          const ta = a.addedAt ? new Date(a.addedAt).getTime() : -Infinity;
          const tb = b.addedAt ? new Date(b.addedAt).getTime() : -Infinity;
          return tb - ta; // newest first; null addedAt sorts last
        })
      : [];

  return (
    <section className="admin__section" aria-label="Partners allowlist">
      <header className="admin__section-head">
        <h2 className="admin__section-title">Partners</h2>
        <p className="admin__section-sub">
          Domains and individual emails permitted to access this site. Changes take effect on
          the next sign-in.
        </p>
      </header>

      {/* Add form — always visible (let the server reject if state is wrong) */}
      <div className="admin__card">
        <h3 className="admin__card-title">Add entry</h3>
        <AddForm
          onAdded={triggerReload}
          onForbidden={handleForbidden}
          getIdToken={getIdToken}
        />
      </div>

      {/* List */}
      <div className="admin__card admin__card--table">
        <h3 className="admin__card-title">
          Current entries
          {state.phase === 'ready' && (
            <span className="admin__count">{state.entries.length}</span>
          )}
        </h3>

        {state.phase === 'loading' && (
          <p className="admin__loading" aria-live="polite">Loading…</p>
        )}

        {state.phase === 'error' && (
          <div className="admin__list-error" role="alert">
            <p>{state.message}</p>
            <button
              type="button"
              className="admin__btn admin__btn--secondary"
              onClick={triggerReload}
            >
              Retry
            </button>
          </div>
        )}

        {state.phase === 'ready' && sorted.length === 0 && (
          <p className="admin__empty">No partners yet — add a domain or email below.</p>
        )}

        {state.phase === 'ready' && sorted.length > 0 && (
          <div className="admin__table-wrap">
            <table className="admin__table" aria-label="Allowlist entries">
              <thead>
                <tr>
                  <th className="admin__th">Type</th>
                  <th className="admin__th">Value</th>
                  <th className="admin__th">Label</th>
                  <th className="admin__th">Status</th>
                  <th className="admin__th">Expires</th>
                  <th className="admin__th">Added by</th>
                  <th className="admin__th">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((entry) => (
                  <EntryRow
                    key={entry.id}
                    entry={entry}
                    onMutated={triggerReload}
                    onForbidden={handleForbidden}
                    getIdToken={getIdToken}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
